// src/pages/ProjectDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { projectsAPI } from '../services/api';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProject = async () => {
      try {
        console.log('Versuche Projektdaten zu laden, ID:', id);
        const response = await projectsAPI.getById(id);
        console.log('Projektdaten erfolgreich geladen:', response.data);
        setProject(response.data.project);
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Projektdaten:', err);
        
        // Prüfen, ob es ein Authentifizierungsfehler ist
        if (err.response && err.response.status === 401) {
          // Manuell zur Login-Seite umleiten, wenn nicht authentifiziert
          console.log('Nicht authentifiziert, leite zur Login-Seite um');
          navigate('/login');
          return;
        }
        
        setError('Fehler beim Laden der Projektdaten');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id, navigate]);

  // Status in lesbaren Text umwandeln
  const getStatusText = (status) => {
    const statusMap = {
      planned: 'Geplant',
      in_progress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert'
    };
    return statusMap[status] || status;
  };

  // Status-Farbe bestimmen
  const getStatusColor = (status) => {
    const colorMap = {
      planned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="text-center py-6">Lade Projektdaten...</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
        <Link to="/projekte" className="text-primary-600 hover:text-primary-900">
          Zurück zur Projektliste
        </Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-4">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
          <p className="text-yellow-700">Projekt nicht gefunden</p>
        </div>
        <Link to="/projekte" className="text-primary-600 hover:text-primary-900">
          Zurück zur Projektliste
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Projektdetails</h1>
        <div className="space-x-3">
          <Link
            to={`/projekte/${id}/edit`}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
          >
            Bearbeiten
          </Link>
          <Link
            to="/projekte"
            className="border border-gray-300 px-4 py-2 rounded text-gray-700 hover:bg-gray-100"
          >
            Zurück
          </Link>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-medium mb-4">Projektinformationen</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Titel</dt>
                <dd className="mt-1 text-gray-900">{project.title}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Kunde</dt>
                <dd className="mt-1 text-gray-900">
                  {project.company_name ? 
                    `${project.company_name} (${project.first_name} ${project.last_name})` : 
                    `${project.first_name} ${project.last_name}`
                  }
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                    {getStatusText(project.status)}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Erstellt am</dt>
                <dd className="mt-1 text-gray-900">
                  {new Date(project.created_at).toLocaleDateString('de-DE')}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {project.description && (
          <div className="mt-6">
            <h2 className="text-xl font-medium mb-3">Beschreibung</h2>
            <div className="bg-gray-50 p-4 rounded border border-gray-200">
              <p className="text-gray-700 whitespace-pre-line">{project.description}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailPage;
